import React, { useEffect, useRef, useState } from "react";
import p5 from "p5";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import colors from "../../styles/colors";
import { PlaybackButton } from "../StyledButton/StyledButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";

interface MusicVisualizerProps {
  width?: number;
  height?: number;
  audioFile: string;
  isPlaying: boolean;
  onPlayingChange: (playing: boolean) => void;
}

const MusicVisualizer: React.FC<MusicVisualizerProps> = ({
  width = 800,
  height = 400,
  audioFile,
  isPlaying,
  onPlayingChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:900px)');
  
  const scaledWidth = isMobile ? width * 0.6 : width;
  const scaledHeight = isMobile ? height * 0.6 : height;
  const timelineWidth = isMobile ? 200 : 400;

  const containerRef = useRef<HTMLDivElement>(null);
  const sketchRef = useRef<p5 | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create audio context and elements
    audioContextRef.current = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 128;

    // Create gain node for volume control
    gainNodeRef.current = audioContextRef.current.createGain();
    gainNodeRef.current.gain.value = volume;

    audioRef.current = new Audio(audioFile);
    audioRef.current.loop = true;

    // Set up audio event listeners
    audioRef.current.addEventListener("loadedmetadata", () => {
      setDuration(audioRef.current?.duration || 0);
    });

    audioRef.current.addEventListener("timeupdate", () => {
      setCurrentTime(audioRef.current?.currentTime || 0);
    });

    // Create p5 instance
    const sketch = new p5((p: p5) => {
      const dataArray = new Uint8Array(analyserRef.current!.frequencyBinCount);
      const rows = isMobile ? 30 : 40;  // Reduce grid density for mobile
      const cols = isMobile ? 30 : 40;
      const points: number[][] = [];
      let noiseOffset = 0;

      p.setup = () => {
        // Enable WEBGL mode properly
        const canvas = p.createCanvas(scaledWidth, scaledHeight, p.WEBGL);
        p.smooth();
        p.frameRate(30);
        // Initialize points array
        for (let i = 0; i < rows; i++) {
          points[i] = new Array(cols).fill(0);
        }
      };

      p.draw = () => {
        // Set background color
        p.background(226, 226, 226);
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);

        // Sample and smooth the frequency data more efficiently
        const sampledData = new Array(cols);
        const samplingRate = Math.floor(dataArray.length / cols);
        for (let i = 0; i < cols; i++) {
          let sum = 0;
          for (let j = 0; j < samplingRate; j++) {
            sum += dataArray[i * samplingRate + j] || 0;
          }
          sampledData[i] = sum / samplingRate;
        }

        // Shift old data up more efficiently
        points.copyWithin(0, 1);
        points[rows - 1] = sampledData.map((val) =>
          p.map(val, 0, 255, 0, scaledHeight / 2)
        );

        // Set up 3D view with WEBGL transformations
        p.translate(0, isMobile ? 90 : 150, isMobile ? 70 : -100);
        p.rotateX(p.PI / 2);
        p.rotateZ(p.PI / 2);

        // Draw the 3D terrain with improved quality
        p.stroke(150, 150, 150);
        p.strokeWeight(isMobile ? 0.8 : 1);
        p.fill(226, 226, 226);

        // Adjust the origin to center the grid
        p.translate(-scaledWidth / 2, -scaledHeight / 2, 0);

        const xStep = scaledWidth / (cols - 1);
        const yStep = scaledHeight / (rows + 1);
        noiseOffset += isMobile ? 0.08 : 0.1;

        // Pre-calculate noise values for the frame with smoother interpolation
        const noiseVals = new Array(rows * cols);
        const noiseScale = isMobile ? 0.06 : 0.08;
        const noiseAmplitude = isMobile ? 3 : 5;
        
        for (let i = 0; i < rows; i++) {
          for (let j = 0; j < cols; j++) {
            noiseVals[i * cols + j] =
              p.noise(j * noiseScale, i * noiseScale, noiseOffset) * noiseAmplitude;
          }
        }

        // Draw triangles between points with smoother interpolation
        for (let i = 0; i < rows - 1; i++) {
          p.beginShape(p.TRIANGLE_STRIP);
          for (let j = 0; j < cols; j++) {
            const x = j * xStep;
            const y1 = (i + 1) * yStep;
            const y2 = (i + 2) * yStep;

            const z1 = points[i][j] + noiseVals[i * cols + j];
            const z2 = points[i + 1][j] + noiseVals[(i + 1) * cols + j];

            p.vertex(x, y1, z1);
            p.vertex(x, y2, z2);
          }
          p.endShape();
        }
      };
    }, containerRef.current);

    sketchRef.current = sketch;

    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (sketchRef.current) {
        sketchRef.current.remove();
        sketchRef.current = null;
      }
    };
  }, [width, height, audioFile, isMobile, scaledWidth, scaledHeight]);

  // Effect to handle play/pause changes from props
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Handle volume changes
  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume;
    }
  };

  // Format time in MM:SS
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Handle timeline click
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Handle play/pause
  const togglePlayPause = async () => {
    if (
      audioRef.current &&
      audioContextRef.current &&
      analyserRef.current &&
      gainNodeRef.current
    ) {
      try {
        if (!sourceRef.current) {
          await audioContextRef.current.resume();
          sourceRef.current = audioContextRef.current.createMediaElementSource(
            audioRef.current
          );
          sourceRef.current.connect(analyserRef.current);
          analyserRef.current.connect(gainNodeRef.current);
          gainNodeRef.current.connect(audioContextRef.current.destination);
        }

        if (isPlaying) {
          audioRef.current.pause();
          onPlayingChange(false);
        } else {
          audioRef.current.play();
          onPlayingChange(true);
        }
      } catch (error) {
        console.error("Error toggling play/pause:", error);
      }
    }
  };

  return (
    <Box className="music-visualizer">
      <Box
        ref={containerRef}
        sx={{
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
        }}
      />

      <Box
        className="controls"
        sx={{
          width: `${scaledWidth}px`,
          backgroundColor: colors.lightgray,
          border: `1px solid ${colors.gray}`,
        }}
      >
        <Box
          className="actions"
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Box
            className="playback-controls"
            sx={{
              display: "flex",
              justifyContent: "center",
              marginLeft: isMobile ? "0.5rem" : "1rem",
            }}
          >
            <PlaybackButton
              onClick={togglePlayPause}
              sx={{
                padding: isMobile ? "6px" : "8px",
                "& svg": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </PlaybackButton>
          </Box>
          <Box
            className="time"
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"center"}
            gap={isMobile ? "0.25rem" : "0.5rem"}
            margin={isMobile ? "0.5rem 0" : "1rem 0"}
          >
            <Box 
              width={isMobile ? "40px" : "50px"} 
              textAlign={"right"} 
              color={colors.antracite}
              sx={{ fontSize: isMobile ? "0.8rem" : "1rem" }}
            >
              {formatTime(currentTime)}
            </Box>
            <Box
              className="timeline"
              onClick={handleTimelineClick}
              sx={{
                width: `${timelineWidth}px`,
                height: isMobile ? "0.8rem" : "1.1rem",
                backgroundColor: colors.gray,
                cursor: "pointer",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: `${(currentTime / duration) * 100}%`,
                  height: "100%",
                  width: "2px",
                  backgroundColor: colors.antracite,
                }}
              />
            </Box>
            <Box 
              width={isMobile ? "40px" : "50px"} 
              textAlign={"left"} 
              color={colors.antracite}
              sx={{ fontSize: isMobile ? "0.8rem" : "1rem" }}
            >
              {formatTime(duration)}
            </Box>
          </Box>
          <Box
            className="volume-controls"
            marginRight={isMobile ? "0.5rem" : "1rem"}
            display="flex"
            alignItems="center"
          >
            <VolumeUpIcon
              sx={{
                color: colors.gray,
                marginRight: isMobile ? "0.25rem" : "0.5rem",
                fontSize: isMobile ? "1rem" : "1.2rem",
              }}
            />
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              style={{
                width: isMobile ? "60px" : "100px",
                WebkitAppearance: "none",
                appearance: "none",
                height: "4px",
                background: colors.gray,
                borderRadius: "2px",
                outline: "none",
                opacity: 1,
                transition: "opacity .2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseOut={(e) => (e.currentTarget.style.opacity = "0.7")}
            />
            <style>
              {`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: ${isMobile ? "10px" : "12px"};
                  height: ${isMobile ? "10px" : "12px"};
                  background: ${colors.antracite};
                  border-radius: 50%;
                  cursor: pointer;
                }
                input[type="range"]::-moz-range-thumb {
                  width: ${isMobile ? "0.6rem" : "0.8rem"};
                  height: ${isMobile ? "0.6rem" : "0.8rem"};
                  background: ${colors.antracite};
                  border-radius: 50%;
                  cursor: pointer;
                  border: none;
                }
              `}
            </style>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MusicVisualizer;
